/**
 * Strikeout Overlay Component
 *
 * Handles text selection and strikeout line creation on PDF pages.
 * Uses window.getSelection() for text selection detection.
 */

import React, { useCallback, useRef, useState } from 'react';
import { FieldContextMenu } from './FieldContextMenu';
import styles from './StrikeoutOverlay.module.scss';

export interface Strikeout {
  id: string;
  // TWO-PHASE PAGE TRACKING
  pageId: string;    // Stable page UUID
  pageNum: number;   // Derived from pageId
  x: number;
  y: number;
  width: number;
  height: number;
  color?: string; // Strikeout color (hex)
  text?: string; // Preview of struck-out text
  groupId: string; // Groups multi-line strikeouts from same selection
}

export interface StrikeoutOverlayProps {
  strikeouts: Strikeout[];
  pageNum: number;
  pageWidth: number;
  pageHeight: number;
  zoom: number; // Current zoom level for coordinate conversion
  strikeoutMode: boolean; // True when strikeout tool is active
  onAddStrikeout: (strikeout: Omit<Strikeout, 'id' | 'pageId'>) => void; // Handler adds id and pageId
  onDeleteStrikeout?: (id: string) => void;
  canDeleteStrikeouts?: boolean; // Whether user can delete strikeouts (Full Editing only)
}

export const StrikeoutOverlay: React.FC<StrikeoutOverlayProps> = ({
  strikeouts,
  pageNum,
  pageWidth,
  pageHeight,
  zoom,
  strikeoutMode,
  onAddStrikeout,
  onDeleteStrikeout,
  canDeleteStrikeouts = true, // Default to true for backward compatibility
}) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const [contextMenuId, setContextMenuId] = useState<string | null>(null);
  const [contextMenuPos, setContextMenuPos] = useState<{ x: number; y: number } | null>(null);

  // Filter strikeouts for this page
  const pageStrikeouts = strikeouts.filter(s => s.pageNum === pageNum);

  /**
   * Handle click on strikeout - show context menu at click position
   * Only allow if user has permission to delete strikeouts
   */
  const handleStrikeoutClick = useCallback((e: React.MouseEvent, strikeoutId: string) => {
    // Don't show context menu if user can't delete strikeouts
    if (!canDeleteStrikeouts) {
      return;
    }

    e.stopPropagation();
    const target = e.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    setContextMenuId(strikeoutId);
    // Position menu at click location (horizontally) and below the strikeout (vertically)
    setContextMenuPos({ x: e.clientX, y: rect.bottom + 4 });
  }, [canDeleteStrikeouts]);

  /**
   * Handle delete from context menu - deletes entire group
   */
  const handleDelete = useCallback(() => {
    if (contextMenuId && onDeleteStrikeout) {
      // Find the clicked strikeout to get its groupId
      const clickedStrikeout = strikeouts.find(s => s.id === contextMenuId);

      if (clickedStrikeout) {
        // Delete all strikeouts in the same group
        strikeouts
          .filter(s => s.groupId === clickedStrikeout.groupId)
          .forEach(s => onDeleteStrikeout(s.id));
      }
    }
    setContextMenuId(null);
    setContextMenuPos(null);
  }, [contextMenuId, strikeouts, onDeleteStrikeout]);


  return (
    <>
      <div
        ref={overlayRef}
        className={`${styles.overlay} ${strikeoutMode ? styles.strikeoutMode : ''}`}
        style={{ width: pageWidth, height: pageHeight }}
      >
        {pageStrikeouts.map(strikeout => {
          // Create larger clickable area (8px tall) centered on the strikeout line
          const clickAreaHeight = 8;
          const lineOffset = clickAreaHeight / 2;

          return (
            <div
              key={strikeout.id}
              className={`${styles.strikeoutLine} ${contextMenuId === strikeout.id ? styles.selected : ''}`}
              style={{
                left: `${strikeout.x * zoom}px`,
                top: `${(strikeout.y * zoom) - lineOffset}px`, // Center clickable area on line
                width: `${strikeout.width * zoom}px`,
                height: `${clickAreaHeight}px`, // Fixed height for consistent clickable area
                cursor: canDeleteStrikeouts ? 'pointer' : 'default', // Only show pointer if deletable
              }}
              onClick={(e) => handleStrikeoutClick(e, strikeout.id)}
              title={strikeout.text || 'Strikeout'}
            />
          );
        })}
      </div>

      {/* Context menu - positioned below selected strikeout */}
      {contextMenuPos && contextMenuId && (
        <FieldContextMenu
          x={contextMenuPos.x}
          y={contextMenuPos.y}
          fieldType="strikeout"
          onDelete={handleDelete}
          onClose={() => {
            setContextMenuId(null);
            setContextMenuPos(null);
          }}
        />
      )}
    </>
  );
};
